<script setup>

import CountdownTimer from "@shared/components/atoms/CountdownTimer.vue"
import CounterNumberValue from "@shared/components/atoms/CounterNumberValue.vue"
import InputCheckbox from "@shared/components/atoms/form/InputCheckbox.vue"
import InputRadio from "@shared/components/atoms/form/InputRadio.vue"
import InputSelect from "@shared/components/atoms/form/InputSelect.vue"
import InputText from "@shared/components/atoms/form/InputText.vue"
import AlertModal from "@shared/components/modals/AlertModal.vue"
import useHelpers from "@shared/composables/useHelpers"
import {useAccountStore} from "@shared/stores/AccountStore"
import {useCouponStore} from "@shared/stores/CouponStore"
import {useLoaderStore} from "@shared/stores/LoaderStore"
import {useNotificationsStore} from "@shared/stores/NotificationsStore"
import {useTransactionsStore} from "@shared/stores/TransactionsStore"
import {useWalletStore} from "@shared/stores/WalletStore"
import useVuelidate from "@vuelidate/core"
import {helpers, required, requiredIf} from "@vuelidate/validators"
import {useDebounceFn} from "@vueuse/core"
import {addMinutes} from "date-fns"
import {computed, onMounted, reactive, watch} from "vue"
import {Vue3SlideUpDown} from "vue3-slide-up-down"

const accountStore = useAccountStore()
const notificationStore = useNotificationsStore()
const walletStore = useWalletStore()
const transactionStore = useTransactionsStore()
const couponStore = useCouponStore()

const {timeout} = useHelpers()

const couponMinDeposit = computed(() => {
    if (couponStore?.activeCoupon?.min_deposit) {
        return couponStore.activeCoupon.min_deposit
    }

    return 0
})

const depositAmountModel = computed(() => {
    const minDeposit = Math.trunc(couponMinDeposit.value)
    const addBonusMatch = state.form.deposit.addBonusMatch

    return {
        pattern: helpers.withMessage(
            `Deposit amount must be greater than R${minDeposit && addBonusMatch ? minDeposit : 0}`,
            (value) => minDeposit && addBonusMatch
                ? value >= minDeposit
                : value > 0
        )
    }
})

const rules = computed(() => ({
    deposit: {
        amount: {
            ...depositAmountModel.value,
            requiredIf: requiredIf(() => state.activeTab === 'deposit'),
        },
        customerID: {},
        accountNumber: {
            requiredIf: requiredIf(isPayment3AbsaSelected),
            pattern: helpers.withMessage(
                'Account Number must have at most 10 digits.',
                (value) => sanitizeAccountNumber(value).length <= 10
            ),
        },
        paymentOption: {
            required,
        },
        paymentOptionSubType: {},
        addBonusMatch: {},
    },
    withdraw: {
        amount: {
            required,
        },
        accountType: {
            required,
        },
        bank: {
            required,
        },
        accountNumber: {
            required,
        },
        accountHolder: {
            required,
            pattern: helpers.withMessage(
                'Invalid value',
                (value) => /^[a-zA-Z0-9\s]*$/.test(value)
            ),
        },
    },
}))

const state = reactive({
    activeTab: 'deposit',
    confirmation: {
        amount: 0,
        balance: 0,
        internal_transaction_id: null,
        status: null,
    },
    form: {
        deposit: {
            amount: '',
            paymentOption: '',
            paymentOptionSubType: '',
            customerID: accountStore.user.customer_id || '',
            accountNumber: '',
            addBonusMatch: false,
        },
        withdraw: {
            amount: '',
            accountType: '',
            bank: '',
            accountNumber: '',
            accountHolder: '',
        }
    },
    payment3Timeout: null,
    isProcessing: false,
    isQueuedWithdrawal: false,
    isPayment3ModalVisible: false,
    isDiscoveryBankEnabled: false,
    paymentKey: '',
    paymentProvider: '',
    withdrawalTransactionId: null,
})

const isPayment3AbsaSelected = computed(() => state.form.deposit.paymentOption === 'payment3' && state.form.deposit.paymentOptionSubType === 'absa')

const sanitizeAccountNumber = (value) => {
    if(!value || typeof value !== 'string') {
        return ''
    }

    return value.replace(/\D/g, '').replace(/^0+/, '')
}

const depositForm = useVuelidate(() => rules.value.deposit, state.form.deposit)
const withdrawalForm = useVuelidate(() => rules.value.withdraw, state.form.withdraw)

const isWithdrawalAllowed = computed(() => walletStore.canWithdraw && walletStore.depositWagerPercentage >= 100)

const payment3SubTypeTitle = computed(() =>
            state.form.deposit.paymentOption === 'payment3'
                ? (state.form.deposit.paymentOptionSubType === 'absa' ? 'Absa' : 'Capitec')
                : ''
        )

const setPaymentOption = (option, subType = '') => {
    depositForm.value.paymentOption.$model = option
    depositForm.value.paymentOptionSubType.$model = subType
}

const handleDepositClick = useDebounceFn(() => handleDeposit(), 100, {maxWait: 100})

const handleDeposit = async () => {
    if(state.isProcessing) {
        return
    }

    state.paymentKey = ''
    state.paymentProvider = ''

    depositForm.value.$touch()

    if (!depositForm.value.$invalid) {
        const loaderStore = useLoaderStore()
        try {
            state.isProcessing = true
            loaderStore.increment()

            const {
                key = null,
                token = null,
                queuedWithdraw = false,
                provider = null
            } = await walletStore.postDeposit({
                addBonusMatch: +state.form.deposit.addBonusMatch,
                accountNumber: sanitizeAccountNumber(state.form.deposit.accountNumber),
                amount: state.form.deposit.amount,
                customerId: state.form.deposit.customerID,
                paymentGateway: walletStore.getDepositPaymentGateway(state.form.deposit.paymentOption),
                paymentSubType: state.form.deposit.paymentOptionSubType,
                paymentType: walletStore.getDepositPaymentType(state.form.deposit.paymentOption),
            }) || {}

            state.isProcessing = false

            if (key) {
                const now = new Date()
                state.paymentProvider = provider || walletStore.getDepositPaymentGateway(state.form.deposit.paymentOption)

                switch (state.paymentProvider) {
                    case 'payment3':
                        loaderStore.decrement()
                        state.isPayment3ModalVisible = true
                        state.payment3Timeout = addMinutes(now, 2)

                        validateDeposit({paymentId: key, provider: state.paymentProvider})
                        break

                    case 'callpay':
                        // eslint-disable-next-line no-undef
                        eftSec.checkout.init({
                            paymentKey: key,
                            paymentType: walletStore.getDepositPaymentType(state.form.deposit.paymentOption),
                            onLoad: () => {
                                loaderStore.stop()
                            },
                            onComplete: (data) => {
                                // eslint-disable-next-line no-undef
                                eftSec.checkout.hideFrame()

                                console.log(data)

                                if(data.success) {
                                    validateDeposit({
                                        paymentId: data.merchant_reference,
                                        provider: state.paymentProvider,
                                        transaction_id: data.transaction_id,
                                    })
                                } else {
                                    notificationStore.showAlert('Your deposit has failed. Please try again.')
                                }
                            },
                            callbackAfterPayment: () => {
                            },
                            serviceUrl: "{protocol}://payments.payguru.io/rpp-transaction/create-from-key"
                        })
                        break

                    case 'worldsafepay':
                        loaderStore.decrement()

                        // eslint-disable-next-line no-undef
                        worldSafePay.initCheckout({
                            paymentKey: key,
                            onLoad: () => {
                                loaderStore.decrement()
                            },
                            callbackAfterPayment: (response) => {
                                const {status} = response
                                if (status === 'Failed') {
                                    notificationStore.showAlert('Your deposit has failed. Please try again.')
                                } else if (status === 'Cancelled') {
                                    notificationStore.showAlert('Your deposit has been cancelled.')
                                } else {
                                    validateDeposit({paymentId: key, provider: state.paymentProvider})
                                }
                            }
                        })
                        break

                    case 'paypump':
                        // eslint-disable-next-line no-undef
                        var myEftFrame = new MyEftFrame(token)
                        myEftFrame.txnUrl = key
                        myEftFrame.Show()

                        window.MyEftReturn = async () => {
                            // eslint-disable-next-line no-undef
                            const returnUrl = myeftreturnurl
                            const paymentId = returnUrl.indexOf("payment_id=") > 0 ? returnUrl.split("payment_id=")[1] : ""

                            if (paymentId) {
                                await validateDeposit({paymentId, provider: state.paymentProvider})
                            }

                            loaderStore.decrement()
                        }

                        loaderStore.decrement()
                        break
                    default:
                        window.location.href = key

                }
            } else {
                loaderStore.decrement()
                if (queuedWithdraw) {
                    notificationStore.showAlert('Your deposit has been successful, but you have a pending withdrawal. Please reverse your pending withdrawal in your Transaction History before making another withdrawal.')
                }
            }
        } catch (e) {
            loaderStore.decrement()
            notificationStore.showAlert(e)
            state.isProcessing = false
        }
    }
}

const validateDeposit = async (payload) => {
    try {
        let attempts = 0
        let ms = 2500
        let data = null

        while (
            attempts < 5 || (state.paymentProvider === 'payment3' && state.payment3Timeout)
        ) {
            data = await walletStore.validateDeposit(payload)
            if (['completed', 'cancelled', 'success'].includes(data.status.toLowerCase())) {
                break
            }

            if(state.paymentProvider === 'payment3' && data.is_callback_received) {
                break
            }

            attempts++

            if(state.paymentProvider !== 'payment3') {
                ms = attempts * 1000
            }
            await timeout(ms)
        }

        state.confirmation.amount = parseFloat(data.amount)
        state.confirmation.balance = parseFloat(data.balance)
        state.confirmation.internal_transaction_id = data.internal_transaction_id
        state.confirmation.status = data.status

        state.isPayment3ModalVisible = false
        state.payment3Timeout = null

        walletStore.fetchWallet()
        accountStore.fetchUser(true)
    } catch (e) {
        notificationStore.showAlert('Unable to validate deposit. Please contact support.')
    }
}

const handleTransactionConfirmClick = () => {
    if (state.confirmation.status === 'success') {
        walletStore.setWalletModalVisibility(false)
    }

    state.confirmation.amount = 0
    state.confirmation.balance = 0
    state.confirmation.internal_transaction_id = null
    state.confirmation.status = null
}

const handleWithdraw = async () => {
    if (!isWithdrawalAllowed.value) {
        notificationStore.showAlert('You are not allowed to withdraw at this time. Please contact support.')
        return
    }

    withdrawalForm.value.$touch()
    if (!withdrawalForm.value.$invalid) {
        try {
            const {internal_transaction_id = null, queuedWithdraw = false} = await walletStore.postWithdrawal({
                amount: state.form.withdraw.amount,
                accountType: state.form.withdraw.accountType,
                bank: state.form.withdraw.bank,
                accountNumber: state.form.withdraw.accountNumber,
                accountHolder: state.form.withdraw.accountHolder
            }) || {}
            state.withdrawalTransactionId = internal_transaction_id
            state.isQueuedWithdrawal = queuedWithdraw
        } catch (e) {
            console.log(e)
            notificationStore.showAlert(e)
        }
    }
}

watch(
    () => walletStore.depositBonusPercentage,
    (newValue) => {
        state.form.deposit.addBonusMatch = newValue > 0
    }
)

watch(
    () => accountStore.user.customer_id,
    (newValue) => {
        state.form.deposit.customerID = newValue
    }
)

watch(
    () => accountStore.user.absa_account_number,
    (newValue) => {
        state.form.deposit.accountNumber = newValue
    }
)

onMounted(function () {
    window.addEventListener('message', (event) => {
        if (typeof event.data == 'string') {
            if (
                event.data.includes('eftSec.') ||
                event.data.includes('eftsecure_checkout_app') ||
                event.data.includes('.location')
            ) {
                eval(event.data)
            }
        }
    })

    if (accountStore.isAuthenticated) {
        couponStore.fetchActiveCoupon()
    }
})

</script>

<template>

    <div
        v-if="walletStore.isWalletModalVisible"
        id="divAppWallet"
        :class="{
            show: true,
            withdraw: state.activeTab === 'withdraw',
            depositBonus: walletStore.depositBonusPercentage > 0
        }"
    >
        <div class="divWalletOverlay"></div>
        <div class="divWalletPopup">
            <div id="divWalletUsername">
                {{ accountStore.user.name }}
            </div>

            <div id="divWalletMoneyBalance">
                Balance:
                <br/>
                <span>
                    <CounterNumberValue
                        :count="walletStore.getMoneyBalance"
                        :decimal-places="2"
                        prefix="R"
                    />
                </span>
            </div>

            <div id="divWalletBonusBalance">
                Bonus balance:
                <br/>
                <span>
                    <CounterNumberValue
                        :count="walletStore.getBonusBalance"
                        :decimal-places="2"
                        prefix="R"
                    />
                </span>
            </div>

            <div
                id="btnCloseWallet"
                @click="() => walletStore.setWalletModalVisibility(false)"
            ></div>

            <div id="tbnWalletView">
                <div class="divTabNavigator fill">
                    <div
                        @click="() => state.activeTab = 'deposit'"
                        :class="{
                            divButton: true,
                            selected: state.activeTab === 'deposit'
                        }"
                        data-index="0"
                        style="width: 50%;"
                    >DEPOSIT
                    </div>
                    <div
                        @click="() => state.activeTab = 'withdraw'"
                        :class="{
                            divButton: true,
                            selected: state.activeTab === 'withdraw'
                        }"
                        data-index="1"
                        style="width: 50%;"
                    >WITHDRAW
                    </div>
                </div>
            </div>

            <div class="divDepositForm">
                <div id="txtDepositAmount">
                    <InputText
                        v-model="depositForm.amount.$model"
                        :validator="depositForm.amount"
                        label="Deposit amount (ZAR)"
                        type="number"
                        style="text-align: center;"
                    />
                </div>

                <div id="depositGw"></div>

                <div :class="{
                    'divPaymentOptions': true,
                    'divPaymentOptions--error': depositForm.paymentOption.$error
                }">
                    <div class="divPaymentOptions__items">
                        <div
                            v-if="walletStore.isVoucherUpEnabled"
                            @click="() => setPaymentOption('voucherUp')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'voucherUp'
                            }"
                            style="display: none;"
                        >
                            <div class="divPaymentOptionIcon fa fa-money"></div>
                            <div>VoucherUp</div>
                        </div>
                        <div
                            v-if="walletStore.isPayment3AbsaEnabled"
                            @click="() => setPaymentOption('payment3', 'absa')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'payment3' && depositForm.paymentOptionSubType.$model === 'absa'
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-money"></div>
                            <div>ABSA PAY</div>
                        </div>
                        <div
                            v-if="walletStore.isCapitecEnabled"
                            @click="() => setPaymentOption('capitec')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'capitec'
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-money"></div>
                            <div>Capitec</div>
                        </div>
                        <div
                            v-if="walletStore.isPayment3Enabled"
                            @click="() => setPaymentOption('payment3')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'payment3' && depositForm.paymentOptionSubType.$model === ''
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-money"></div>
                            <div>Capitec Pay</div>
                        </div>
                        <div
                            @click="() => setPaymentOption('eft')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'eft'
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-university"></div>
                            <div>Instant EFT</div>
                        </div>
                        <div
                            @click="() => setPaymentOption('creditCard')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'creditCard'
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-credit-card-alt"></div>
                            <div>Card</div>
                        </div>
                        <!-- Last Payment Option -->
                        <div v-if="walletStore.isBitcoinEnabled && useBitcoinPayments"
                            @click="() => setPaymentOption('bitcoin')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'bitcoin'
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-btc"></div>
                            <div>Bitcoin</div>
                        </div>
                        <div v-else-if="state.isDiscoveryBankEnabled"
                            @click="() => setPaymentOption('discoveryBank')"
                            :class="{
                                'divPaymentOption': true,
                                'selected': depositForm.paymentOption.$model === 'discoveryBank'
                            }"
                        >
                            <div class="divPaymentOptionIcon fa fa-money"></div>
                            <div>Discovery bank</div>
                        </div>
                    </div>
                    <div class="divPaymentOptions__error">
                        <Vue3SlideUpDown v-model="depositForm.paymentOption.$error">
                            <div class="validationError">
                                Value is required
                            </div>
                        </Vue3SlideUpDown>
                    </div>
                </div>

                <template v-if="state.form.deposit.paymentOption === 'payment3'">
                    <div id="txtDepositAmount">
                        <InputText
                            v-if="!depositForm.paymentOptionSubType.$model"
                            v-model="depositForm.customerID.$model"
                            :validator="depositForm.customerID"
                            label="13 digit SA Identity Number"
                            type="number"
                            style="text-align: center;"
                            :disabled="accountStore.user.customer_id && accountStore.user.customer_id_verified_at"
                        />
                        <InputText
                            v-else-if="depositForm.paymentOptionSubType.$model === 'absa'"
                            v-model="depositForm.accountNumber.$model"
                            :validator="depositForm.accountNumber"
                            label="ABSA Account Number"
                            type="number"
                            style="text-align: center;"
                        />
                    </div>
                </template>

                <div
                    v-if="walletStore.depositBonusPercentage > 0"
                    style="display: flex; justify-content: center"
                >
                    <InputCheckbox
                        v-model="depositForm.addBonusMatch.$model"
                    >
                        Apply {{ walletStore.depositBonusPercentage }}% bonus coupon
                    </InputCheckbox>
                </div>

                <button
                    class="btnGreen"
                    @click="handleDepositClick"
                >
                    DEPOSIT
                </button>
            </div>

            <div class="divWithdrawForm">
                <div id="txtWithdrawAmount">
                    <InputText
                        v-model="withdrawalForm.amount.$model"
                        :validator="withdrawalForm.amount"
                        label="Withdraw amount (ZAR)"
                        type="number"
                        style="text-align: center;"
                    />
                </div>
                <div id="drdBank">
                    <InputSelect
                        v-model="withdrawalForm.bank.$model"
                        :validator="withdrawalForm.bank"
                        label="Bank"
                        style="text-align: center;"
                        :options="{
                            Absa: 'Absa',
                            Afribank: 'Afribank',
                            Bidvest: 'Bidvest',
                            Capitec: 'Capitec',
                            Discovery: 'Discovery',
                            Fnb: 'FNB',
                            Investec: 'Investec',
                            Mercantile: 'Mercantile',
                            Nedbank: 'Nedbank',
                            Oldmutual: 'Oldmutual',
                            Sasfin: 'Sasfin',
                            Standard: 'Standard',
                            Tyme: 'Tyme'
                        }"
                    />
                </div>
                <div class="divAccountTypes">
                    <InputRadio
                        v-model="withdrawalForm.accountType.$model"
                        :validator="withdrawalForm.accountType"
                        :options="{
                            cheque: 'Cheque',
                            savings: 'Savings',
                            transmission: 'Transmission'
                        }"
                    />
                </div>
                <div id="txtAccountNumber">
                    <InputText
                        v-model="withdrawalForm.accountNumber.$model"
                        :validator="withdrawalForm.accountNumber"
                        label="Account number"
                        type="number"
                        style="text-align: center;"
                    />
                </div>
                <div id="txtAccountHolder">
                    <InputText
                        v-model="withdrawalForm.accountHolder.$model"
                        :validator="withdrawalForm.accountHolder"
                        label="Account holder"
                        type="text"
                        style="text-align: center;"
                    />
                </div>

                <button @click="handleWithdraw" class="btnGreen">WITHDRAW</button>

                <div
                    v-if="!isWithdrawalAllowed"
                    id="divDisableOverlay"
                    class="show"
                >
                    <span v-if="!walletStore.canWithdraw">
                        Withdrawals has been disabled on your account. To enable withdrawals again, please contact support.
                    </span>
                    <span v-else-if="!walletStore.depositWagerPercentage < 100">
                        You must wager at least 1 x your deposit amount before you qualify to queue a withdrawal. You have already wagered <span>{{
                            walletStore.depositWagerPercentage
                        }}%</span> of your deposit value.
                    </span>
                </div>
            </div>
            <div
                id="divTransactionHistoryLink"
                @click="() => transactionStore.setTransactionModalVisibility(true)"
            >
                Transaction history
            </div>
        </div>

        <AlertModal
            v-if="state.withdrawalTransactionId"
            @click:confirm="() => state.withdrawalTransactionId = null"
        >
            <div>
                Withdrawal successfully queued.
                Your withdrawal will reflect as successful in your transaction history, once it's been processed.
            </div>

            Transaction ID:
            <span style='color:#0cd35d'>
                {{ state.withdrawalTransactionId }}
            </span>
        </AlertModal>

        <AlertModal
            v-if="state.isQueuedWithdrawal"
            cancel-text="TRANSACTION HISTORY"
            @click:cancel="() => transactionStore.setTransactionModalVisibility(true)"
            @click:confirm="() => state.isQueuedWithdrawal = false"
        >
            Please note: You will need to reverse your pending withdrawal in your Transaction History before making
            another withdrawal. When you reverse a pending withdrawal the balance is instantly available in your
            {{ appName }} wallet.
        </AlertModal>

        <AlertModal
            confirm-text="OK"
            v-if="state.confirmation.amount"
            @click:confirm="handleTransactionConfirmClick"
        >
            <div style="display: flex; gap: 15px; flex-direction: column;">

                <template v-if="state.confirmation.status === 'success'">
                    <h3 class="textGreen" style="font-weight: 600; text-transform: uppercase;">
                        Transaction successful
                    </h3>
                    <div>
                        Your deposit of
                        <span class="textGreen">
                            <CounterNumberValue :count="state.confirmation.amount" :decimal-places="2" prefix="R"/>
                        </span>
                        was successful.
                        Your new balance is
                        <span class="textGreen">
                            <CounterNumberValue :count="state.confirmation.balance" :decimal-places="2" prefix="R"/>
                        </span>.
                    </div>

                    <div>
                        Transaction ID:
                        <span class="textGreen">{{ state.confirmation.internal_transaction_id }}</span>
                    </div>
                </template>
                <template v-else>
                    <h3 class="textRed" style="font-weight: 600; text-transform: uppercase;">
                        Transaction error
                    </h3>
                    <div>
                        Your deposit of
                        <span class="textRed">
                            <CounterNumberValue :count="state.confirmation.amount" :decimal-places="2" prefix="R"/>
                        </span>
                        was unsuccessful.
                    </div>

                    <div>
                        Transaction ID:
                        <span class="textRed">{{ state.confirmation.internal_transaction_id }}</span>
                    </div>
                </template>
            </div>
        </AlertModal>

        <AlertModal
            v-if="state.isPayment3ModalVisible"
            confirm-text="CANCEL TRANSACTION"
            @click:confirm="() => state.isPayment3ModalVisible = false"
            style="max-width: 95vw; width: 500px; display: flex; flex-direction: column; align-items: center;"
        >
            <h3 class="textGreen" style="font-weight: 600; text-transform: uppercase;">
                {{payment3SubTypeTitle}} Pay approval
            </h3>

            <h1 style="display: flex; justify-content: center; padding: 1em 0;">
                <CountdownTimer
                    label=""
                    :date="state.payment3Timeout"
                    :is-styled="false"
                    @timer:end="() => {
                        state.isPayment3ModalVisible = false
                        state.payment3Timeout = null
                        notificationStore.showAlert(payment3SubTypeTitle + ' Payment timeout. Please try again.')
                    }"
                />
            </h1>

            Payment Instructions:
            <ul v-if="isPayment3AbsaSelected">
                <li>
                    <span class="textGreen">SIGN IN:</span>
                    Sign in to ABSA Banking App
                </li>
                <li>
                    <span class="textGreen">APPROVE PAYMENT:</span>
                    Approve payment on the confirmation screen
                </li>
                <li>
                    <span class="textGreen">Not receiving Absa Pay Request?</span>
                    Close your ABSA App and reopen
                </li>
                <li>
                    <span class="textGreen">COMPLETE PAYMENT</span>
                </li>
            </ul>

            <ul v-else>
                <li>
                    <span class="textGreen">SIGN IN:</span>
                    Sign in to Capitec Pay App
                </li>
                <li>
                    <span class="textGreen">APPROVE PAYMENT:</span>
                    Approve Payment on the confirmation screen
                </li>
                <li>
                    <span class="textGreen">Already Signed in?</span>
                    If you are already signed in to the Capitec App, tap Transact then {{payment3SubTypeTitle}} Pay to approve the Payment.
                </li>
                <li>
                    <span class="textGreen">COMPLETE PAYMENT</span>
                </li>
            </ul>

            Please go to your {{payment3SubTypeTitle}} App and approve the payment within 2 minutes.
        </AlertModal>
    </div>
</template>

<style lang="scss">
.tingle-modal__close {
    background: none;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
}

.tingle-modal__close svg, .tingle-modal__close span {
    width: 25px;
    height: 25px;
}

.divPaymentOptions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;

    &__items {
        display: flex;
        width: 100%;
    }

    &__error {
        color: red;
        font-size: 11px;
    }

    &--error {
        .divPaymentOption {
            border-color: red !important;
        }
    }
}
</style>
